import { useEffect, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { handleMutation } from '@gv/triage-components';

import { Config } from 'config';
import { useAppSelector } from 'store';
import { selectAuthForGVTalk } from 'store/slices/auth';
import { UserActionsContext } from 'context/user-actions';
import { usePushSubscribeMutation } from 'store/api/users';

const urlBase64ToUint8Array = (base64String: string) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

export const useWebPush = () => {
  const { isUserClickHappened } = useContext(UserActionsContext);
  const location = useLocation();
  const gvTalkAuth = useAppSelector(selectAuthForGVTalk);
  const authProfileId = gvTalkAuth?.authProfileId;

  const [subscribe, subscribeMutation] = usePushSubscribeMutation();
  handleMutation(subscribeMutation);

  // Store profile ID in IndexedDB and send to service worker
  const storeProfileId = async (profileId: number | undefined) => {
    if (!profileId) {
      return;
    }

    try {
      // Store in IndexedDB for persistence
      await new Promise<void>((resolve, reject) => {
        const request = indexedDB.open('gv-push-notifications', 1);

        request.onerror = () => reject(request.error);
        request.onsuccess = () => {
          const db = request.result;
          const transaction = db.transaction(['profile'], 'readwrite');
          const store = transaction.objectStore('profile');
          const putRequest = store.put(profileId, 'profileId');
          putRequest.onsuccess = () => resolve();
          putRequest.onerror = () => reject(putRequest.error);
        };

        request.onupgradeneeded = (event) => {
          const db = (event.target as IDBOpenDBRequest).result;
          if (!db.objectStoreNames.contains('profile')) {
            db.createObjectStore('profile');
          }
        };
      });

      // Send to service worker via postMessage
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready;
        if (registration.active) {
          registration.active.postMessage({
            profileId,
            type: 'SET_USER_PROFILE_ID',
          });
        }
      }
    } catch (error) {
      console.error('Error storing profile ID:', error);
    }
  };

  useEffect(() => {
    // Store profile ID whenever it changes
    if (authProfileId) {
      storeProfileId(authProfileId);
    }
  }, [authProfileId]);

  useEffect(() => {
    // Only enable web push for pet owners
    if (!Config.portalType.isPetOwner) {
      return;
    }

    const registerPushNotifications = async () => {
      try {
        const registration = await navigator.serviceWorker.register(
          '/web-push-sw.js',
          {
            scope: '/',
          }
        );
        await registration.update();

        // Send profile ID to service worker after registration
        if (authProfileId && registration.active) {
          registration.active.postMessage({
            profileId: authProfileId,
            type: 'SET_USER_PROFILE_ID',
          });
        }

        const result = await Notification.requestPermission();
        if (result === 'denied') {
          console.error('The user explicitly denied the permission request.');
          return;
        }
        if (result === 'granted') {
          console.info('The user accepted the permission request.');
        }

        const subscribed = await registration.pushManager.getSubscription();
        if (subscribed) {
          await subscribe(subscribed);
          return;
        }

        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(Config.vapidKey!),
        });
        await subscribe(subscription);
      } catch (error) {
        console.error(error);
      }
    };
    if (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      isUserClickHappened &&
      Config.vapidKey
    ) {
      registerPushNotifications();
    }
  }, [isUserClickHappened, location.pathname, authProfileId]);
};
